import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  IconCircleUserOutline18,
  IconEnvelopeOutline18,
  IconTrashOutline18,
} from 'nucleo-ui-outline-18'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { openChangePasswordModal } from '~/components/account/change-password-modal'
import { ProfileAvatarField } from '~/components/profile/profile-avatar-field'
import {
  SettingsPage,
  SettingsReadOnlyValue,
  SettingsRow,
  SettingsSection,
} from '~/components/settings/settings-page'
import { SettingsTextField } from '~/components/settings/settings-text-field'
import { Button } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { confirm } from '~/lib/confirm'
import { useLocale } from '~/lib/locale-store'
import { splitDisplayName } from '~/lib/person-name'
import { makeFirstNameSchema, makeLastNameSchema } from '~/lib/profile'
import { beginSignOut, markSignOutInProgress } from '~/lib/router-auth'
import { validateWith } from '~/lib/validation'
import { api } from '~convex/_generated/api'
import type { Doc } from '~convex/_generated/dataModel'
import { convexErrorCode } from '~convex/lib/errorCodes'

const SUPPORT_EMAIL = 'contato@planno.app'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/account/profile')({
  component: ProfileSettings,
})

function ProfileSettings() {
  const { data: user } = useQuery(convexQuery(api.users.currentUser, {}))
  const { data: uploadsEnabled } = useQuery(convexQuery(api.users.avatarUploadsEnabled, {}))
  // False — so the control stays hidden — for a Google or Apple account,
  // which has no password to change, and while staff are impersonating.
  const { data: canChangePassword } = useQuery(convexQuery(api.accountPassword.canChange, {}))

  return (
    <SettingsPage>
      {user === undefined ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <ProfileFields
          canChangePassword={canChangePassword === true}
          uploadsEnabled={uploadsEnabled === true}
          user={user}
        />
      )}
    </SettingsPage>
  )
}

/**
 * The profile is workspace-independent, but its settings live under the
 * workspace URL alongside everything else — one settings surface rather than
 * two. Each field commits on its own, so the surname is sent alongside the
 * given name (and vice versa) to satisfy `updateProfile`'s pair argument.
 *
 * `user` is `null` only in the sliver between sign-out and the auth gate's
 * redirect firing — nothing to edit, so render nothing rather than an empty
 * form.
 */
const ProfileFields = ({
  canChangePassword,
  user,
  uploadsEnabled,
}: {
  canChangePassword: boolean
  user: Doc<'users'> | null
  uploadsEnabled: boolean
}) => {
  const { t, i18n } = useLingui()
  const updateProfile = useConvexMutation(api.users.updateProfile)
  const firstNameSchema = useMemo(() => makeFirstNameSchema(i18n), [i18n])
  const lastNameSchema = useMemo(() => makeLastNameSchema(i18n), [i18n])

  if (user === null) {
    return null
  }

  // Falls back to splitting `name` — an OAuth signup has no first/last.
  const { firstName, lastName } = splitDisplayName(user)

  return (
    <>
      <SettingsSection icon={IconCircleUserOutline18} title={t`Who you are`}>
        <SettingsRow htmlFor="firstName" label={t`First name`}>
          <SettingsTextField
            autoComplete="given-name"
            id="firstName"
            onSave={(value) => updateProfile({ firstName: value, lastName })}
            validate={(value) => validateWith(firstNameSchema, value)}
            value={firstName}
          />
        </SettingsRow>

        <SettingsRow htmlFor="lastName" label={t`Last name`}>
          <SettingsTextField
            autoComplete="family-name"
            id="lastName"
            onSave={(value) => updateProfile({ firstName, lastName: value })}
            validate={(value) => validateWith(lastNameSchema, value)}
            value={lastName}
          />
        </SettingsRow>

        <SettingsRow
          description={t`PNG, JPG or WebP, up to 15 MB. Shown at 512×512.`}
          label={t`Avatar`}
        >
          <ProfileAvatarField
            avatarConfig={user.avatarConfig}
            image={user.image ?? null}
            name={user.name ?? user.firstName ?? '?'}
            uploadsEnabled={uploadsEnabled}
            userId={user._id}
          />
        </SettingsRow>
      </SettingsSection>

      {user.email !== undefined && (
        <SettingsSection icon={IconEnvelopeOutline18} title={t`Sign-in`}>
          <SettingsRow
            description={<Trans>Your sign-in email can't be changed here yet.</Trans>}
            label={t`Email`}
          >
            <SettingsReadOnlyValue>{user.email}</SettingsReadOnlyValue>
          </SettingsRow>

          {/* Only for accounts that sign in with a password — there is
              nothing to rotate on a Google or Apple account, so the row is
              absent rather than disabled. */}
          {canChangePassword ? (
            <SettingsRow
              description={<Trans>Signs you out of every other browser and device.</Trans>}
              label={t`Password`}
            >
              <Button
                className="w-full sm:w-auto"
                onClick={() => openChangePasswordModal({ email: user.email ?? '' })}
                type="button"
                variant="outline"
              >
                <Trans>Change password</Trans>
              </Button>
            </SettingsRow>
          ) : null}
        </SettingsSection>
      )}

      <DeleteAccountSection />
    </>
  )
}

/**
 * Closing your account, from the web.
 *
 * It exists here because the closure is account-wide, not app-wide: the flag
 * refuses sign-in and denies live sessions everywhere, so a web-only user
 * could otherwise find themselves locked out of a door they had no way to
 * lock. The phone got the button first only because App Review asks for it.
 *
 * The copy is the same promise the mobile screen makes, and it is the same
 * careful promise: closed now, erased later, reversible by writing to us.
 * Do not upgrade it to "permanently deleted" — or even to "scheduled for
 * removal" — while the mechanism is a flag and no erasure job exists.
 */
const DeleteAccountSection = () => {
  const { t } = useLingui()
  const { locale } = useLocale()
  const { mutateAsync: requestAccountDeletion, isPending } = useMutation({
    mutationFn: useConvexMutation(api.users.requestAccountDeletion),
  })

  const handleDelete = async () => {
    const confirmed = await confirm({
      cancelLabel: t`Keep my account`,
      confirmLabel: t`Delete my account`,
      description: t`You won't be able to sign in again with this email, on any device. Your studio's contents have not been erased. They stay stored with us until we remove them for good. Changed your mind? Write to ${SUPPORT_EMAIL} and we can reopen it.`,
      destructive: true,
      icon: <IconTrashOutline18 />,
      title: t`Delete your account?`,
    })
    if (!confirmed) {
      return
    }
    try {
      // The confirmation email is the only place the reversal path is ever
      // communicated, so it has to arrive in the language they were reading.
      await requestAccountDeletion({ locale })
    } catch (error) {
      if (convexErrorCode(error) === 'OWNS_SHARED_WORKSPACE') {
        // Refused rather than silently leaving a team with an owner who can
        // never sign in. Name the way out.
        toast.error(
          t`You own a studio other people work in. Transfer it to someone else, or remove its members, and then come back.`
        )
        return
      }
      console.error('Request account deletion error:', error)
      toast.error(t`Couldn't close your account. Try again, or write to us.`)
      return
    }
    // The account is already closed server-side; this only drops the local
    // session. Same teardown the sign-out menu item uses, so the authed tree
    // unmounts before the token goes and the backend isn't handed a burst of
    // `Not authenticated` errors on the way out.
    markSignOutInProgress()
    beginSignOut()
  }

  return (
    <SettingsSection
      description={
        <Trans>
          Closing your account is immediate and cannot be undone from here. Write to {SUPPORT_EMAIL}{' '}
          if you change your mind.
        </Trans>
      }
      icon={IconTrashOutline18}
      title={t`Delete account`}
    >
      <SettingsRow
        description={
          <Trans>
            You will not be able to sign in again with this email. Your studio's contents stay
            stored with us until we remove them for good.
          </Trans>
        }
        label={t`Delete account`}
      >
        <Button disabled={isPending} onClick={handleDelete} type="button" variant="destructive">
          <Trans>Delete my account</Trans>
        </Button>
      </SettingsRow>
    </SettingsSection>
  )
}
